import React, { useEffect, useState } from 'react';
import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Header from '../../shared/components/layout/Header';
import Search from '../../shared/components/ui/Search';
import { transformData } from '../../super-admin/utils/transformData';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import {
  createExtraCharges,
  getAllExtraCharges,
  updateExtraCharges,
  getAllAccommodationsByProject,
  deleteExtraCharge,
  handleCheckExtraChargeDelete
} from '../services/api/implementation/impl';
import { CreateExtraCharges } from '../components/forms/CreateExtraCharges';
import { UpdateExtraCharges } from '../components/forms/UpdateExtraCharges';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import Cookies from 'js-cookie';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import { Plus, ChevronDown, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import editIcon from '../../shared/assets/svg/edit.svg';
import deleteIcon from '../../shared/assets/svg/delete.svg';

export const ExtraChargesList = ({ projectName }) => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const [selectedAccommodationType, setSelectedAccommodationType] = useState('ALL');
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenUpdateModal, setIsOpenUpdateModal] = useState(false);
  const [selectedExtraCharge, setSelectedExtraCharge] = useState(null);
  const [extraChargesList, setExtraChargesList] = useState([]);
  const [pricingType, setPricingType] = useState('all');
  const [accommodations, setAccommodations] = useState({});
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const { collapsed } = useSidebar();
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  const measurementTypeWithoutSlash = measurementType.replace('/', '');

  const { register } = useForm({
    defaultValues: {
      pricingType: ''
    }
  });

  // Query to get all accommodations
  const { data: accommodationData } = useQuery({
    queryKey: ['accommodations', projectId, selectedAccommodationType],
    queryFn: () => {
      const queryParams =
        selectedAccommodationType !== 'ALL' ? `&accomodationType=${selectedAccommodationType}` : '';
      return getAllAccommodationsByProject(projectId, queryParams);
    },
    enabled: !!projectId
  });

  // Query to get all extra charges with filters
  const { data: allExtraCharges, refetch: refetchExtraCharges } = useQuery({
    queryKey: ['getAllExtraCharges', projectId, selectedAccommodationType],
    queryFn: () => {
      const queryParams =
        selectedAccommodationType !== 'ALL'
          ? `project_id=${projectId}&accomodationType=${selectedAccommodationType}`
          : `project_id=${projectId}`;
      return getAllExtraCharges(queryParams);
    },
    enabled: !!projectId,
    onError: (error) => {
      console.error('Error fetching extra charges:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to fetch extra charges'
      );
    }
  });

  const { data: ExtraCharges, refetch: fetchExtraCharges } = useQuery({
    queryKey: ['ExtraCharges', projectId, selectedAccommodationType],
    queryFn: () => getAllExtraCharges(`project_id=${projectId}`),
    enabled: !!projectId,
    onError: (error) => {
      console.error('Error fetching extra charges:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to fetch extra charges'
      );
    }
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Extra Charges List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchExtraCharges();
    fetchExtraCharges();
  }, []);

  // useEffect(() => {
  //   if (ExtraCharges && Array.isArray(ExtraCharges) && ExtraCharges.length > 0) {
  //     const firstPricingType = ExtraCharges.find((charge) => charge.mode !== 'all')?.mode;
  //     if (firstPricingType) {
  //       setPricingType(firstPricingType);
  //     }
  //   }
  // }, [allExtraCharges]);

  // Update accommodations map when accommodation data changes
  useEffect(() => {
    if (accommodationData) {
      const accommodationMap = {};
      accommodationData.forEach((acc) => {
        accommodationMap[acc.id] = acc.name;
      });
      setAccommodations(accommodationMap);
    }
  }, [accommodationData]);

  // Format data for table
  const extraChargesFormat = [
    'accommodation_name',
    'name',
    'description',
    'price',
    'actions',
    'id'
  ];

  const handleExtraChargesData = (item) => {
    return {
      id: item.id,
      accommodation_name: accommodations[item.accomodation_id] || '---',
      name: item.name,
      description: item.description,
      price: `₹${new Intl.NumberFormat('en-IN').format(item.price)}`,
      mode: item.mode,
      actions: `Actions---${item.id}`
    };
  };

  // Update table data when extra charges or accommodations change
  useEffect(() => {
    if (allExtraCharges && Array.isArray(allExtraCharges)) {
      const formattedData = allExtraCharges.map((el) =>
        transformData(handleExtraChargesData(el), extraChargesFormat)
      );
      setExtraChargesList(formattedData);
    } else {
      setExtraChargesList([]);
    }
  }, [allExtraCharges, accommodations]);

  // Create extra charge mutation
  const { mutate: createExtraCharge } = useMutation({
    mutationFn: (payload) => createExtraCharges(payload),
    onSuccess: (data) => {
      toast.success('Extra charge created successfully');
      setIsOpenCreateModal(false);
      if (data?.mode && data.mode !== 'all') {
        setPricingType(data.mode);
      }
      refetchExtraCharges();
    },
    onError: (err) => {
      console.error('Error creating extra charge:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create extra charge');
    }
  });

  // Update extra charge mutation
  const { mutate: updateExtraCharge } = useMutation({
    mutationFn: ({ id, payload }) => updateExtraCharges(id, payload),
    onSuccess: () => {
      toast.success('Extra charge updated successfully');
      setIsOpenUpdateModal(false);
      refetchExtraCharges();
    },
    onError: (err) => {
      console.error('Error updating extra charge:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update extra charge');
    }
  });

  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
  };

  const handleEdit = (id) => {
    const extraCharge = allExtraCharges?.find((charge) => charge.id === Number(id));
    if (extraCharge) {
      setSelectedExtraCharge(extraCharge);
      setIsOpenUpdateModal(true);
    } else {
      toast.error('Extra charge not found');
    }
  };

  // Function to get display value for pricing type
  const getPricingTypeDisplay = () => {
    if (!pricingType || pricingType === 'all') return '';

    if (measurementTypeWithoutSlash === 'sqmt' && pricingType === 'sqft') {
      return 'sqmt';
    }

    switch (pricingType) {
      case 'accommodation':
        return 'Accommodation';
      case 'sqft':
        return 'sqft';
      case 'sqmt':
        return 'sqmt';
      default:
        return pricingType;
    }
  };

  //delete functions

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckExtraChargeDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const charge = allExtraCharges?.find((item) => parseInt(item.id) === parseInt(id));
      const ECName = charge?.name || 'extra charge';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: ECName });
        setSelectedIds([parseInt(id)]);
      } else {
        // Fallback if not found
        setResData({ ...res, name: ECName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteExtraCharge(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Extra Charge Deleted Successfully');
      refetchExtraCharges();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting etxra charge', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting extra charge');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  // Header dropdown state and portal for Accommodation filter
  const [openDropdown, setOpenDropdown] = useState(null);
  const accHeaderRef = React.useRef(null);
  const [accDropdownRect, setAccDropdownRect] = useState(null);

  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    const dropdownRef = React.useRef(null);

    React.useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          onClose && onClose();
        }
      };
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          onClose && onClose();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [onClose]);

    return createPortal(
      <div
        ref={dropdownRef}
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type, e) => {
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }
    if (type === 'accommodation' && accHeaderRef?.current) {
      setAccDropdownRect(accHeaderRef?.current?.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  const handleSelectAccommodationFilter = (type) => {
    setOpenDropdown(null);
    setSelectedAccommodationType(type);
  };

  return (
    <div className="w-full relative h-full ">
      {isOpenCreateModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Create Extra Charge"
          onClose={() => setIsOpenCreateModal(false)}
          width={60}>
          <CreateExtraCharges
            projectName={projectName}
            createSuccess={createExtraCharge}
            onClose={() => setIsOpenCreateModal(false)}
            existingPricingType={pricingType !== 'all' ? pricingType : null}
            measurementTypeWithoutSlash={measurementTypeWithoutSlash}
          />
        </Modal>
      )}

      {isOpenUpdateModal && selectedExtraCharge && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Update Extra Charge"
          onClose={() => setIsOpenUpdateModal(false)}
          width={60}>
          <UpdateExtraCharges
            extraCharge={selectedExtraCharge}
            updateSuccess={updateExtraCharge}
            onClose={() => setIsOpenUpdateModal(false)}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this extra charge'}'`}
          onClose={() => setCanDelete(null)}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this extra charge has dependencies, please delete following sold units to
              proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'extra charges'}
            onClose={() => setCanDelete(null)}
            data={extraChargesList}
            type={'extraCharges'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      {/* Grid */}
      <GridWrapper width={100}>
        <div className="w-full overflow-clip bg-gray-100">
          <div className="fixed bottom-8 right-8 z-50">
            <Button
              displayType="primary"
              onClick={() => setIsOpenCreateModal(true)}
              customStyle={{
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
              <Plus size={24} color="white" />
            </Button>
          </div>

          <div className=" w-full relative rounded-t-lg mt-3 accommodation-table-container h-[calc(100vh-10.5rem)] overflow-auto">
            <style>{`
                .accommodation-table-container .table-body {
                  position: static !important;
                  overflow-y: visible !important;
                  max-height: none !important;
                  height: auto !important;
                  top: auto !important;
                  padding-bottom: 0 !important;
                }
                .accommodation-table-container .table-container {
                  height: auto !important;
                }
                .accommodation-table-container > div {
                  height: auto !important;
                }
              `}</style>

            <table className="text-left w-full">
              <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                <tr className="font-normal h-[3rem] w-full">
                  <th className="px-4 py-2 border-b font-normal">Name</th>
                  {/* <th
                      className="px-4 py-2 border-b font-normal relative w-[250px]"
                      ref={accHeaderRef}>
                      <span>Accommodation Type</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('accommodation', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'accommodation' && accDropdownRect && (
                        <PortalDropdown
                          anchorRect={accDropdownRect}
                          onClose={() => setOpenDropdown(null)}>
                          {['ALL', 'GROUP', 'INDEPENDENT'].map((opt) => (
                            <div
                              key={opt}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                selectedAccommodationType === opt && 'bg-blue-100'
                              }`}
                              onClick={() => handleSelectAccommodationFilter(opt)}>
                              {opt}
                            </div>
                          ))}
                        </PortalDropdown>
                      )}
                    </th> */}
                  <th className="px-4 py-2 border-b font-normal">Associated Accommodations</th>
                  <th className="px-4 py-2 border-b font-normal">Pricing Type</th>
                  <th className="px-4 py-2 border-b font-normal">Price</th>
                  <th className="px-4 py-2 border-b font-normal">GST (%)</th>
                  <th className="px-4 py-2 border-b font-normal">Required / Optional</th>
                  <th className="px-4 py-2 border-b font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(allExtraCharges) && allExtraCharges.length > 0 ? (
                  allExtraCharges.map((item, idx) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                        <div className="relative inline-flex group shrink-0">
                            <Info
                              className="w-4 h-4 text-gray-400 hover:text-customBlue cursor-help"
                              aria-label="View description"
                            />
                            <div className="absolute left-0 bottom-full mb-1 px-2 py-1.5 text-xs bg-gray-900 text-white rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-30 max-w-[220px] whitespace-normal">
                              {item.description || 'No description'}
                            </div>
                          </div>
                          <span>{item.name || '---'}</span>
                         
                        </div>
                      </td>
                      {/* <td className="px-4 py-2">{item.accomodation.accomodationType || '---'}</td> */}
                      <td className="px-4 py-2">
                        {item.accomodations?.map((acc) => acc.accomodation?.name).join(', ') ||
                          '---'}
                      </td>
                      <td className="px-4 py-2">{item.mode || '---'}</td>
                      <td className="px-4 py-2">
                        ₹{new Intl.NumberFormat('en-IN').format(item.price || 0)}
                      </td>
                      <td className="px-4 py-2">
                        {item.gst != null && item.gst !== '' ? (
                          <>
                            {Number(item.gst).toFixed(2)}% (₹{new Intl.NumberFormat('en-IN').format((Number(item.price) || 0) * (Number(item.gst) || 0) / 100)})
                          </>
                        ) : (
                          '---'
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {item.isMandatory === true || item.isMandatory === 'true' ? 'Required' : 'Optional'}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-start w-full relative gap-3">
                          <div
                            className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item.id);
                            }}>
                            <img src={editIcon} alt="edit__icon" />
                          </div>
                          <div
                            className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCheckDelete(item.id);
                            }}>
                            <img src={deleteIcon} alt="delete__icon" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-10 align-middle">
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                        <p className="text-gray-600 text-sm">
                          No extra charges found for the selected accommodation type.
                          <br />
                          Proceed with adding new extra charges.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </GridWrapper>
    </div>
  );
};

export default ExtraChargesList;
